import React, { useMemo } from 'react'
import { WelcomePageStep } from './components/WelcomePageStep'
import { EmobilityTabsStep } from './components/EmobilityTabsStep'
import { DashboardChartFiltersStep } from './components/dashboard-steps/DashboardChartFiltersStep'
import { DashboardContainerStep } from './components/dashboard-steps/DashboardContainerStep'
import { DashboardStatisticsStep } from './components/dashboard-steps/DashboardStatisticsStep'
import { DashboardTableFiltersStep } from './components/dashboard-steps/DashboardTableFiltersStep'
import { TariffsStep } from './components/tariffs-steps/TariffsStep'
import { AddTariffDialogStep } from './components/tariffs-steps/AddTariffDialogStep'
import { TariffsMenuStep } from './components/tariffs-steps/TariffsMenuStep'
import { DynamicTariffDetailsStep } from './components/tariffs-steps/DynamicTariffDetailsStep'
import { WallboxCardStep } from './components/wallbox-steps/WallboxCardStep'
import { AssignTariffToWallboxStep } from './components/wallbox-steps/AssignTariffToWallboxStep'
import { AssignTariffToWallboxDialogStep } from './components/wallbox-steps/AssignTariffToWallboxDialogStep'
import { ChargingSessionsTableStep } from './components/charging-sessions-steps/ChargingSessionsTableStep'
import { ChargingSessionsExportStep } from './components/charging-sessions-steps/ChargingSessionsExportStep'
import { ChargingCardsMenuStep } from './components/charging-cards-steps/ChargingCardsMenuStep'
import { EditChargingCardDialogStep } from './components/charging-cards-steps/EditChargingCardDialogStep'
import { LinkChargingCardMenuStep } from './components/charging-cards-steps/LinkChargingCardMenuStep'
import { OwnersPermissionsStep } from './components/permissions-steps/OwnersPermissionsStep'
import { UsersPermissionsStep } from './components/permissions-steps/UsersPermissionsStep'
import { EndOfTourStep } from './components/EndOfTourStep'
import { useMediaQuery, useTheme } from '@mui/material'
import { UsersReadWriteSteps } from './components/permissions-steps/UsersReadWriteSteps'
import { LinkChargingCardNoteStep } from './components/charging-cards-steps/LinkChargingCardNoteStep'
import { mainAppBarId } from '@e3dc-react/shell/Elements/Header/HeaderBar'
import { chargingCardMenuButtonId } from '../charging-cards/components/ChargingCardsTableMenuIcon'
import { GuidedTourStep } from '@e3dc-react/shell/Elements/GuidedTour'

/** our guide defined steps with real content components */
export const useEMobilityTourSteps = (): GuidedTourStep[] => {
  const theme = useTheme()
  const isLaptopOrSmaller = useMediaQuery(theme.breakpoints.down('lg'))
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'))
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return useMemo(
    () => [
      {
        target: `#${mainAppBarId}`,
        content: <WelcomePageStep />,
        placement: 'center',
      },
      {
        target: `#${mainAppBarId}`,
        content: <EmobilityTabsStep />,
        navigateToAfterStep: 'dashboard',
      },
      {
        target: `#e-mobility-dashboard-container`,
        content: <DashboardContainerStep />,
        placement: 'top-end',
      },
      {
        target: `#e-mobility-statistics`,
        content: <DashboardStatisticsStep />,
      },
      {
        target: '#e-mobility-dashboard-table-filters',
        content: <DashboardTableFiltersStep />,
      },
      {
        target: '#e-mobility-dashboard-chart-filter',
        content: <DashboardChartFiltersStep />,
        navigateToAfterStep: 'tariffs',
      },
      {
        target: isMobile ? '#tariff-table' : '#app-header-tab-tariffs',
        content: <TariffsStep />,
        placement: isMobile ? 'center' : 'bottom',
      },
      {
        target: '#e-mobility-add-new-tariff-button',
        content: <AddTariffDialogStep />,
        placement: 'left-end',
      },
      {
        target: '#tariff-table-row-dots-menu',
        content: <TariffsMenuStep />,
        placement: 'left-end',
      },
      {
        target: isMobileOrTablet ? '#tariff-details-button' : '#e-mobility-tariff-table tbody > tr:nth-child(2)',
        content: <DynamicTariffDetailsStep />,
        navigateToAfterStep: 'wallbox',
        placement: 'bottom',
        styles: {
          tooltipContent: {
            paddingBottom: 0,
          },
        },
      },
      {
        target: isMobile ? '#wallbox-card-mobile' : '#wallbox-card',
        content: <WallboxCardStep />,
        placement: 'auto',
      },
      {
        target: '#e-mobility-wallbox-options-menu-button',
        content: <AssignTariffToWallboxStep />,
        placement: 'right',
      },
      {
        target: '#e-mobility-wallbox-options-menu-button',
        content: <AssignTariffToWallboxDialogStep />,
        navigateToAfterStep: 'charging-sessions',
        placement: 'right',
      },
      {
        target: isLaptopOrSmaller ? '#charging-session-mobile-item' : '#e-mobility-charging-sessions-table table thead',
        content: <ChargingSessionsTableStep />,
        placement: isLaptopOrSmaller ? 'bottom-start' : 'auto',
      },
      {
        target: '#e-mobility-charging-sessions-export-button',
        content: <ChargingSessionsExportStep />,
        navigateToAfterStep: 'charging-cards',
      },
      {
        target: `#${chargingCardMenuButtonId}`,
        content: <ChargingCardsMenuStep />,
        placement: 'left',
      },
      {
        target: `#${chargingCardMenuButtonId}`,
        content: <EditChargingCardDialogStep />,
        navigateToAfterStep: 'wallbox',
        placement: 'left',
      },
      {
        target: '#e-mobility-open-charging-card-to-wallbox-link-dialog-button',
        content: <LinkChargingCardMenuStep />,
        styles: (() => {
          /** This enlarges the linking gif since it's very details and tricky to discern on large screens */
          return {
            floater: {
              width: isMobile ? '300px' : '500px',
            },
            tooltip: {
              width: isMobile ? '300px' : '500px',
            },
          }
        })(),
      },
      {
        target: '#e-mobility-open-charging-card-to-wallbox-link-dialog-button',
        content: <LinkChargingCardNoteStep />,
      },
      {
        target: '#wallbox-card',
        content: <OwnersPermissionsStep />,
        placement: 'center',
      },
      {
        target: '#wallbox-card',
        content: <UsersReadWriteSteps />,
        navigateToAfterStep: 'charging-cards',
        placement: 'center',
      },
      {
        target: `#${chargingCardMenuButtonId}`,
        content: <UsersPermissionsStep />,
        placement: 'auto',
      },
      {
        target: `#${mainAppBarId}`,
        content: <EndOfTourStep />,
        placement: 'center',
      },
    ],
    [isMobile, isMobileOrTablet, isLaptopOrSmaller]
  )
}
